import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Cpu, FlaskConical, Scan, LineChart, Layers } from "lucide-react";
import { Link } from "react-router-dom";

export default function Innovation() {
  const { t } = useTranslation();

  const pillars = [
    { icon: <Cpu className="h-6 w-6" />, key: "ai" },
    { icon: <FlaskConical className="h-6 w-6" />, key: "products" },
    { icon: <LineChart className="h-6 w-6" />, key: "learning" },
    { icon: <Scan className="h-6 w-6" />, key: "laser" },
  ];

  return (
    <div className="section" data-testid="innovation-page">
      <div className="container-narrow max-w-4xl">
        {/* Hero */}
        <div className="text-center">
          <span
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 font-bold"
            data-testid="innovation-status-badge"
          >
            <Sparkles className="h-3 w-3" />
            {t("innovation.status_badge")}
          </span>
          <p className="eyebrow mt-6">{t("innovation.eyebrow")}</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-semibold text-navy leading-tight">
            {t("innovation.title")}
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
            {t("innovation.intro")}
          </p>
        </div>

        {/* Vision statement */}
        <div className="mt-12 rounded-2xl bg-gradient-to-br from-[#1B2845] to-[#2E4372] text-white p-8 sm:p-10 shadow-xl">
          <p className="text-lg sm:text-xl leading-relaxed font-light">
            &laquo; {t("innovation.vision_quote")} &raquo;
          </p>
          <p className="mt-4 text-sm text-[#5BA4D4] font-semibold uppercase tracking-widest">
            — Pro-pre R&amp;D
          </p>
        </div>

        {/* 4 pillars */}
        <div className="mt-14">
          <h2 className="text-2xl sm:text-3xl font-semibold text-navy text-center">
            {t("innovation.pillars_title")}
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {pillars.map((p) => (
              <div
                key={p.key}
                className="rounded-2xl border-2 border-slate-100 bg-white p-6 hover:border-[#5BA4D4]/40 transition-all"
                data-testid={`innovation-pillar-${p.key}`}
              >
                <div className="h-12 w-12 rounded-xl bg-[#5BA4D4]/10 text-[#5BA4D4] flex items-center justify-center">
                  {p.icon}
                </div>
                <h3 className="mt-4 text-lg font-semibold text-navy">
                  {t(`innovation.pillar_${p.key}_title`)}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {t(`innovation.pillar_${p.key}_body`)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Ecosystem */}
        <div className="mt-14 rounded-2xl bg-soft p-8">
          <div className="flex items-start gap-3 mb-4">
            <Layers className="h-6 w-6 text-[#5BA4D4] flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-semibold text-navy">
                {t("innovation.ecosystem_title")}
              </h2>
              <p className="text-sm text-slate-500">{t("innovation.ecosystem_sub")}</p>
            </div>
          </div>
          <p className="text-slate-700 leading-relaxed">{t("innovation.ecosystem_body")}</p>
        </div>

        {/* Disclaimer + CTA */}
        <div className="mt-14 rounded-2xl border-2 border-amber-200 bg-amber-50 p-6 text-center">
          <p className="text-sm text-amber-900 font-semibold">{t("innovation.disclaimer_title")}</p>
          <p className="text-sm text-amber-800 mt-2">{t("innovation.disclaimer_body")}</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link to="/devis-instant" className="btn-primary" data-testid="innovation-cta-devis">
              <Sparkles className="h-4 w-4" />
              {t("innovation.cta_devis")}
            </Link>
            <Link to="/reglement" className="btn-outline" data-testid="innovation-cta-reglement">
              {t("innovation.cta_reglement")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
